import { DashboardMarket, SKELETON_DASHBOARD_MARKET } from "@silo/types";
import { cn } from "../../utils/class-names";
import { Panel } from "./components/panel";
import {
  getDebtState,
  getDepositAssets,
  getIsCollateralAsset,
  getIsProtectedAsset,
} from "./utils";
import { ReactNode } from "react";
import { match } from "ts-pattern";
import { CollateralAsset } from "./collateral-asset";
import { ProtectedAsset } from "./protected-asset";
import { BorrowingDisabled, DebtAsset, NoDebt } from "./debt-asset";
import { tv } from "tailwind-variants";
import { buttonV2 } from "../button-v2";
import { useLendingLinkContext } from "./lending-link-context";
import { disjoin, getAssets } from "@silo/utils";
import { Array, pipe } from "effect";
import { PositionMeta } from "./position-meta";
import { SkeletonWrapper } from "../skeleton";

const lendingLink = tv({
  extend: buttonV2,
  defaultVariants: {
    variant: "action",
  },
});

const tokenLogo = tv({
  base: "w-[30px] h-[30px]",
});

export interface LendingPositionProps {
  className?: string;
  dashboardMarket: DashboardMarket;
  tokenLogo: ReactNode;
  depositMoreLink: ReactNode;
  withdrawLink: ReactNode;
  borrowMoreLink: ReactNode;
  repayLink: ReactNode;
  borrowLink: ReactNode;
  includeTitle?: boolean;
  id?: string;
}

export function LendingPosition({
  className,
  dashboardMarket,
  tokenLogo,
  depositMoreLink,
  withdrawLink,
  borrowLink,
  borrowMoreLink,
  repayLink,
  includeTitle = false,
  id,
}: LendingPositionProps): JSX.Element {
  const depositAssets = getDepositAssets(dashboardMarket);
  const hasPositions = depositAssets.length > 0;
  const debtState = getDebtState(dashboardMarket);
  const isLoan = debtState._tag === "hasDebt";

  const showPositionMeta = includeTitle || isLoan;

  return (
    <div id={id} className={cn("@container", className)}>
      <div className="block @screen-sm:hidden">
        <Panel>
          {showPositionMeta && (
            <PositionMeta
              market={dashboardMarket}
              includeTitle={includeTitle}
              isLoan={isLoan}
            />
          )}

          {!hasPositions && <NoPositions market={dashboardMarket} />}

          {depositAssets.map((asset) =>
            match(asset)
              .when(getIsCollateralAsset, () => (
                <CollateralAsset
                  market={dashboardMarket}
                  asset={asset}
                  tokenLogo={tokenLogo}
                  divider
                  isLoan={isLoan}
                  depositMoreLink={depositMoreLink}
                  withdrawLink={withdrawLink}
                />
              ))
              .when(getIsProtectedAsset, () => (
                <ProtectedAsset
                  market={dashboardMarket}
                  asset={asset}
                  tokenLogo={tokenLogo}
                  divider
                  isLoan={isLoan}
                  depositMoreLink={depositMoreLink}
                  withdrawLink={withdrawLink}
                />
              ))
              .otherwise(() => null)
          )}

          {hasPositions &&
            match(debtState)
              .with({ _tag: "disabled" }, () => <BorrowingDisabled />)
              .with({ _tag: "none" }, () => (
                <NoDebt borrowLink={borrowLink} market={dashboardMarket} />
              ))
              .with({ _tag: "hasDebt" }, ({ debtAssets }) =>
                debtAssets.map((asset) => (
                  <DebtAsset
                    market={dashboardMarket}
                    asset={asset}
                    tokenLogo={tokenLogo}
                    divider
                    borrowMoreLink={borrowMoreLink}
                    repayLink={repayLink}
                  />
                ))
              )
              .exhaustive()}
        </Panel>
      </div>

      <div className="hidden @screen-sm:block">
        <Panel>
          <div className="flex flex-col gap-10">
            {showPositionMeta && (
              <PositionMeta
                market={dashboardMarket}
                includeTitle={includeTitle}
                isLoan={isLoan}
              />
            )}

            {!hasPositions && <NoPositions market={dashboardMarket} />}

            {hasPositions && (
              <div className="flex gap-8">
                <div
                  className="flex flex-col flex-1 gap-14"
                  data-cy="deposit-position"
                >
                  {depositAssets.map((asset, i) => (
                    <>
                      {i > 0 && <div className="h-px bg-shale-700" />}

                      {match(asset)
                        .when(getIsCollateralAsset, () => (
                          <CollateralAsset
                            market={dashboardMarket}
                            asset={asset}
                            tokenLogo={tokenLogo}
                            isLoan={isLoan}
                            depositMoreLink={depositMoreLink}
                            withdrawLink={withdrawLink}
                          />
                        ))
                        .when(getIsProtectedAsset, () => (
                          <ProtectedAsset
                            market={dashboardMarket}
                            asset={asset}
                            tokenLogo={tokenLogo}
                            isLoan={isLoan}
                            depositMoreLink={depositMoreLink}
                            withdrawLink={withdrawLink}
                          />
                        ))
                        .otherwise(() => null)}
                    </>
                  ))}
                </div>

                <div className="w-px bg-shale-700" />

                <div
                  className="flex flex-col flex-1 gap-14"
                  data-cy="borrow-position"
                >
                  {match(debtState)
                    .with({ _tag: "disabled" }, () => <BorrowingDisabled />)
                    .with({ _tag: "none" }, () => (
                      <NoDebt
                        borrowLink={borrowLink}
                        market={dashboardMarket}
                      />
                    ))
                    .with({ _tag: "hasDebt" }, ({ debtAssets }) =>
                      debtAssets.map((asset, i) => (
                        <>
                          {i > 0 && <div className="h-px bg-shale-700" />}

                          <DebtAsset
                            market={dashboardMarket}
                            asset={asset}
                            tokenLogo={tokenLogo}
                            borrowMoreLink={borrowMoreLink}
                            repayLink={repayLink}
                          />
                        </>
                      ))
                    )
                    .exhaustive()}
                </div>
              </div>
            )}
          </div>
        </Panel>
      </div>
    </div>
  );
}

LendingPosition.lendingLink = lendingLink;
LendingPosition.tokenLogo = tokenLogo;
LendingPosition.DepositMoreLinkContent = DepositMoreLinkContent;
LendingPosition.WithdrawLinkContent = WithdrawLinkContent;
LendingPosition.BorrowMoreLinkContent = BorrowMoreLinkContent;
LendingPosition.RepayLinkContent = RepayLinkContent;
LendingPosition.BorrowLinkContent = BorrowLinkContent;
LendingPosition.useLendingLinkContext = useLendingLinkContext;

function DepositMoreLinkContent(): JSX.Element {
  return <div>Deposit more</div>;
}

function WithdrawLinkContent(): JSX.Element {
  return <div>Withdraw</div>;
}

function BorrowMoreLinkContent(): JSX.Element {
  return <div>Borrow more</div>;
}

function RepayLinkContent(): JSX.Element {
  return <div>Repay</div>;
}

function BorrowLinkContent(): JSX.Element {
  return <div>Borrow</div>;
}

interface NoPositionsProps {
  market: DashboardMarket;
}

function NoPositions({ market }: NoPositionsProps): JSX.Element {
  const symbols = pipe(
    getAssets(market),
    Array.map((asset) => asset.symbol),
    disjoin
  );

  return (
    <div data-cy="positions-empty" className="flex flex-col gap-2.5">
      <h2 className="font-medium text-white">
        You don{"'"}t have any open positions.
      </h2>
      <p className="text-shale-200">Deposit some {symbols} to get started.</p>
    </div>
  );
}

interface PulseLendingPositionProps {
  className?: string;
}

export function PulseLendingPosition({
  className,
}: PulseLendingPositionProps): JSX.Element {
  return (
    <SkeletonWrapper className={cn("animate-pulse", className)}>
      <LendingPosition
        dashboardMarket={SKELETON_DASHBOARD_MARKET}
        tokenLogo={<div className={tokenLogo()}></div>}
        depositMoreLink={<DepositMoreLinkContent />}
        withdrawLink={<WithdrawLinkContent />}
        borrowMoreLink={<BorrowMoreLinkContent />}
        repayLink={<RepayLinkContent />}
        borrowLink={<BorrowLinkContent />}
      />
    </SkeletonWrapper>
  );
}
